import { useState, useEffect, useRef } from "react";
import promptService from "../services/promptService";
import { displayUserName, formatDisplayDate, truncateString, validateUserRole } from "../helpers/helpers";
import { toast } from "react-toastify";
import { DeletePromptModal } from "../components/ui/prompt/deletePromptModal";
import CreatePromptModal from "../components/ui/prompt/CreatePromptModal";
import UpdatePromptModal from "../components/ui/prompt/UpdatePromptModal";
import { useDashboardUI } from "../context/GlobalContext";

import editIcon from "../assets/edit.svg";
import checkIcon from "../assets/check.svg";
import trashIcon from "../assets/trash.svg";
import xIcon from "../assets/x.svg";
import { userRoles } from "../helpers/constant";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const STATUS_CONFIG = {
  true: {
    label: "Published",
    className: "bg-success-green-100 text-success-green-800",
  },
  false: {
    label: "Unpublished",
    className: "bg-gray-100 text-gray-600",
  },
};

const ACCESS_LEVEL = {
  "facility": "FACILITY",
  "global": "GLOBAL",
  "self": "SELF"
}

export function PromptManagement() {
  const { selectedFacilityFilter, setPageLoader, facilityUserList } = useDashboardUI();

  const [promptList, setPromptList] = useState([]);

  // 🔹 pagination & filters
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [published, setPublished] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null); // "global" | "facility" | null

  const isSuperAdminView = validateUserRole(userRoles.SUPERADMIN);
  // Column widths must sum to 100% in both layouts (with/without the
  // superadmin-only "Access level" column) — otherwise `table-fixed`
  // leaves leftover width that gets distributed unevenly and the header
  // no longer lines up with the row cells below it.
  const colWidths = isSuperAdminView
    ? { name: "18%", desc: "27%", status: "12%", access: "8%", createdBy: "15%", createdAt: "12%", action: "8%" }
    : { name: "20%", desc: "33%", status: "14%", createdBy: "13%", createdAt: "12%", action: "8%" };

  // 🔹 UI state
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [showDeletePromptConfirm, setShowDeletePromptConfirm] = useState(null);
  const [isDeletingPrompt, setIsDeletingPrompt] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [openFilters, setOpenFilters] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");


  const actionMenuRef = useRef(null);
  const filterRef = useRef(null);
  const prevFacilityRef = useRef(selectedFacilityFilter);
  const prevSearchRef = useRef(debouncedSearch);


  //Close ref of dropdown modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setOpenActionMenu(null); // close menu
      }

      // Close filters dropdown
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target)
      ) {
        setOpenFilters(false);
      }

    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // FETCH PROMPTS
  const fetchPromptlist = async () => {
    try {
      setPageLoader(true);
      const facility_number = selectedFacilityFilter?.facility_number;

      let query = `&page=${page}&limit=${limit}`;

      if (facility_number) query += `&facility_number=${facility_number}`;
      if (search) query += `&search=${search}`;
      if (published !== null) query += `&published=${published}`;
      if (accessLevel !== null) query += `&share_access=${accessLevel}`

      const data = await promptService.getAllPrompts(query);

      setPageLoader(false);
      setPromptList(data.results || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      setPageLoader(false);
      console.error("Error fetching prompt list:", error);
    }
  };

  //Call if filter values change — reset page to 1 when facility or search changes
  useEffect(() => {
    const facilityChanged = prevFacilityRef.current !== selectedFacilityFilter;
    const searchChanged = prevSearchRef.current !== debouncedSearch;

    if (facilityChanged || searchChanged) {
      prevFacilityRef.current = selectedFacilityFilter;
      prevSearchRef.current = debouncedSearch;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }

    fetchPromptlist();
  }, [page, limit, published, accessLevel, debouncedSearch, selectedFacilityFilter]);

  //Debounce user typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400); // debounce delay

    return () => clearTimeout(timer);
  }, [search]);


  // Delete Prompt ACTIONS
  const deletePrompt = () => {
    setShowDeletePromptConfirm(promptList[openActionMenu]);
    setOpenActionMenu(null);
  };

  //Update Prompt Status
  const updatePromptStatus = (isPublished) => async () => {
    try {
      setPageLoader(true);
      await promptService.updatePrompt(promptList[openActionMenu].id, {
        is_published: isPublished,
      });
      toast.success("Prompt status updated successfully");
    } catch (error) {
      toast.error("Error updating prompt status");
    } finally {
      setPageLoader(false);
      setOpenActionMenu(null);
      fetchPromptlist();
    }
  };

  //Delete Prompt
  const handlePromptDelete = (promptId) => async () => {
    try {
      setPageLoader(true);
      setIsDeletingPrompt(promptId);
      await promptService.deletePrompt(promptId);
      toast.success("Prompt deleted successfully");
    } catch (error) {
      toast.error("Error deleting prompt");
    } finally {
      setPageLoader(false);
      setShowDeletePromptConfirm(null);
      setIsDeletingPrompt(null);
      fetchPromptlist();
    }
  };

  //Open Update Prompt popup
  const updatePrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setOpenUpdateModal(true);
    setOpenActionMenu(null);
  };


  // ======================================================
  // RENDER
  // ======================================================
  return (
    <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-6">
      {/* Header */}
      <div className="mb-7 flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
        <div className="relative w-full sm:w-[28rem] max-w-full">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search prompt by name or content"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="w-full h-10 rounded-full border border-gray-100 bg-white shadow-sm pl-11 pr-5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Status Filter */}
          <div ref={filterRef} className="flex items-center gap-3 relative">
            {/* More Filters Button */}
            <button
              onClick={() => setOpenFilters((v) => !v)}
              className="flex items-center gap-2 h-10 px-4 rounded-xl border border-transparent bg-white text-sm font-medium text-slate-700 hover:border-violet-200 transition-colors"
            >
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 5h18M6 12h12M10 19h4" />
              </svg>
              More filters
            </button>

            {/* Filters Dropdown */}
            {openFilters && (
              <div className="fixed inset-x-3 top-32 z-[60] w-auto max-h-[calc(100vh-9rem)] overflow-y-auto sm:absolute sm:inset-x-auto sm:top-12 sm:right-0 sm:z-30 sm:w-64 sm:max-h-none sm:overflow-visible rounded-2xl border border-gray-100 bg-white shadow-lg flex flex-col">
                {/* Header */}
                <div className="px-5 pt-4 pb-2 border-b border-gray-100 text-base font-bold text-slate-900 not-italic whitespace-nowrap">
                  Filters
                </div>

                {/* Status Section */}
                <div className="flex flex-col px-5 pt-3 pb-1">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 not-italic whitespace-nowrap">
                    Status
                  </div>

                  <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={published === true}
                      onChange={() => {
                        setPage(1);
                        setPublished(published === true ? null : true);
                      }}
                      className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                    />
                    Published
                  </label>

                  <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={published === false}
                      onChange={() => {
                        setPage(1);
                        setPublished(published === false ? null : false);
                      }}
                      className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                    />
                    Unpublished
                  </label>
                </div>

                {/* Access level Section */}
                {validateUserRole(userRoles.SUPERADMIN) && (
                  <div className="flex flex-col px-5 pt-1 pb-4">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 not-italic whitespace-nowrap">
                      Access Level
                    </div>

                    <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={accessLevel === "global"}
                        onChange={() => {
                          setPage(1);
                          setAccessLevel(accessLevel === "global" ? null : "global");
                        }}
                        className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                      />
                      Global
                    </label>

                    <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={accessLevel === "facility"}
                        onChange={() => {
                          setPage(1);
                          setAccessLevel(accessLevel === "facility" ? null : "facility");
                        }}
                        className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                      />
                      Facility
                    </label>
                  </div>
                )}

              </div>
            )}
          </div>


          {/* Create Button */}
          <button
            onClick={() => setOpenCreateModal(true)}
            className="flex items-center gap-2 rounded-xl bg-blue-500 hover:bg-blue-800 px-4 sm:px-6 h-10 text-sm font-semibold whitespace-nowrap text-white transition-colors"
          >
            Create<span className="hidden sm:inline"> prompt</span> <span className="text-lg leading-none">+</span>
          </button>
        </div>

      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto border border-gray-100 rounded-xl">
        <table className="w-full min-w-[1100px] xl:min-w-[900px] table-fixed text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#F5F2FF] h-11 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th style={{ width: colWidths.name }} className="px-6 text-left">Prompt name</th>
              <th style={{ width: colWidths.desc }} className="px-6 text-left">Description</th>
              <th style={{ width: colWidths.status }} className="px-6 text-left">Status</th>
              {isSuperAdminView && (<th style={{ width: colWidths.access }} className="px-2 text-left whitespace-nowrap">Access level</th>)}
              <th style={{ width: colWidths.createdBy }} className="px-6 text-left">Created by</th>
              <th style={{ width: colWidths.createdAt }} className="px-2 text-left">Created at</th>
              <th style={{ width: colWidths.action }} className="pl-1 pr-2 text-center whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {promptList && promptList.length > 0 ? (
              promptList.map((row, index) => (
                <tr
                  key={row.id}
                  onClick={() => updatePrompt(row)}
                  className="cursor-pointer bg-white border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                >
                  <td style={{ width: colWidths.name }} className="px-6 py-5 text-sm font-semibold text-gray-900">
                    {truncateString(row.name, 50)}
                  </td>

                  <td style={{ width: colWidths.desc }} className="px-6 py-5 text-sm text-gray-500">
                    {truncateString(row.description || row.content, 50)}
                  </td>

                  <td style={{ width: colWidths.status }} className="px-6 py-5">
                    <span
                      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${STATUS_CONFIG[row.is_published]?.className
                        }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {STATUS_CONFIG[row.is_published]?.label}
                    </span>
                  </td>

                  {isSuperAdminView && <td style={{ width: colWidths.access }} className="px-2 py-5 text-sm text-gray-700">
                    {ACCESS_LEVEL[row?.share_access]}
                  </td>}

                  <td style={{ width: colWidths.createdBy }} className="px-6 py-5 text-sm text-gray-700 truncate" title={displayUserName(facilityUserList, row?.created_by)}>{displayUserName(facilityUserList, row?.created_by)}</td>

                  <td style={{ width: colWidths.createdAt }} className="px-2 py-5 text-sm text-gray-700 whitespace-nowrap truncate">
                    {formatDisplayDate(row?.created_at)}
                  </td>

                  <td style={{ width: colWidths.action }} className="relative pl-1 pr-2 py-5 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenActionMenu(openActionMenu === index ? null : index);
                      }}
                      className="rounded-full p-2 hover:bg-gray-100"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="#9CA3AF" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke="#9CA3AF" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke="#9CA3AF" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {openActionMenu === index && (
                      <div ref={actionMenuRef} className="absolute right-6 top-12 z-20 w-52 rounded-xl border bg-white shadow-lg">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePromptStatus(true)();
                          }}
                          disabled={row.is_published}
                          className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-green-600 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <img src={checkIcon} alt="" /> Publish
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePromptStatus(false)();
                          }}
                          disabled={!row.is_published}
                          className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-gray-50"
                        >
                          <img src={xIcon} alt="" /> Unpublish
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePrompt();
                          }}
                          className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 hover:bg-gray-50"
                        >
                          <img src={trashIcon} alt="" /> Delete
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePrompt(row);
                          }}
                          className="flex w-full items-center gap-3 px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-gray-50 border-t border-gray-100"
                        >
                          <img src={editIcon} alt="" /> Update Prompt
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={isSuperAdminView ? 7 : 6}
                  className="px-6 py-10 text-center text-sm text-gray-500"
                >
                  No prompts found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-2 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm">
        <span>Total {total}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1 || total === 0}
              onClick={() => setPage(page - 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl"
            >
              ‹
            </button>

            <span>
              {total === 0 ? "0 / 0" : `${page} / ${totalPages}`}
            </span>

            <button
              disabled={page >= totalPages || total === 0}
              onClick={() => setPage(page + 1)}
              className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <DeletePromptModal
        open={!!showDeletePromptConfirm}
        onClose={() => setShowDeletePromptConfirm(null)}
        onDelete={handlePromptDelete(showDeletePromptConfirm?.id)}
        prompt={showDeletePromptConfirm}
        isDeleting={isDeletingPrompt === showDeletePromptConfirm?.id}
      />

      <CreatePromptModal
        openCreateModal={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onCreated={fetchPromptlist}
      />

      <UpdatePromptModal
        open={openUpdateModal}
        prompt={selectedPrompt}
        onClose={() => setOpenUpdateModal(false)}
        onUpdated={fetchPromptlist}
      />
    </div>
  );
}
